import { Component, EventEmitter, Input, Output } from '@angular/core';
import { OfflineRungs } from '@netfoundry-ui/shared/model';
import { OFFLINE_RUNGS, OfflineRung } from './offline-policy-text';

/**
 * The four rungs of an offline ladder as a table, one {@link DurationInputComponent} per severity. It emits the whole
 * ladder as ISO-8601 durations, and reports while any box holds no positive number.
 */
@Component({
    selector: 'app-offline-ladder-fields',
    templateUrl: './offline-ladder-fields.component.html',
    styleUrls: ['./offline-ladder-fields.component.scss'],
    standalone: false,
})
export class OfflineLadderFieldsComponent {
    @Input() ladder?: OfflineRungs | null;
    /** What each box shows when it is empty: the ladder that applies instead. */
    @Input() placeholders: Partial<Record<keyof OfflineRungs, string>> = {};
    /** The service's message for each rung, already filtered to what the parent wants shown. */
    @Input() errors: Partial<Record<keyof OfflineRungs, string>> = {};
    /** Outlines every box, for a message about the ladder as a whole that the parent shows below it. */
    @Input() allInvalid = false;
    @Input() disabled = false;
    /** Prefixes every element id, so two ladders on one page do not collide. */
    @Input() idPrefix = 'OfflineLadder';
    @Input() label = 'Offline ladder';
    /** `horizontal` puts the severities in the header, as the Network Policy table does; `vertical` fits a narrow dialog. */
    @Input() orientation: 'horizontal' | 'vertical' = 'vertical';

    @Output() ladderChange = new EventEmitter<OfflineRungs>();
    @Output() rungBlur = new EventEmitter<keyof OfflineRungs>();
    /** True while a box holds something that is not a positive number. */
    @Output() invalidChange = new EventEmitter<boolean>();

    readonly rungs = OFFLINE_RUNGS;

    private invalidRungs = new Set<keyof OfflineRungs>();

    setRung(rung: OfflineRung, value: string | null): void {
        const ladder: OfflineRungs = {};
        for (const each of OFFLINE_RUNGS) {
            ladder[each.key] = each.key === rung.key ? value : this.ladder?.[each.key] || null;
        }
        this.ladderChange.emit(ladder);
    }

    setInvalid(rung: OfflineRung, invalid: boolean): void {
        const before = this.invalidRungs.size > 0;
        if (invalid) {
            this.invalidRungs.add(rung.key);
        } else {
            this.invalidRungs.delete(rung.key);
        }
        if (before !== this.invalidRungs.size > 0) {
            this.invalidChange.emit(!before);
        }
    }

    isInvalid(rung: OfflineRung): boolean {
        return this.invalidRungs.has(rung.key);
    }

    errorFor(rung: OfflineRung): string {
        if (this.isInvalid(rung)) {
            return 'Enter a number greater than zero.';
        }
        return this.errors?.[rung.key] || '';
    }
}
